import {
  StyledH1,
  StyledH2,
  StyledLink,
  StyledListItem,
  StyledParagraph,
  StyledSpan,
  StyledSubtitle,
  StyledUnorderedList,
} from './StyledComponents';

export const CookieNotice = () => (
  <div className="flex justify-center">
    <div className="py-[10.4rem] px-4 max-w-full tablet:max-w-[66.67%]">
      <StyledH1>Cookie Notice</StyledH1>
      <StyledSubtitle>Last Updated: March 8, 2023</StyledSubtitle>
      <StyledParagraph>
        Unless otherwise expressly stated, terms in this notice have the same meaning as defined in
        the Privacy Notice.
      </StyledParagraph>

      <StyledH2>1. SCOPE OF NOTICE</StyledH2>
      <StyledParagraph>
        This Cookie Notice supplements the information contained in the Privacy Notice and explains
        how we and our business partners and service providers use cookies and related technologies
        in the course of managing and providing our online services and our electronic communication
        to you. It explains what these technologies are and why we use them, as well as your rights
        to control our use of them.
      </StyledParagraph>
      <StyledParagraph>
        In some cases, we may use cookies and related technologies described in this Cookie Notice
        to collect personal information, or to collect information that becomes personal information
        if we combine it with other information. For more details about how we process your personal
        information, please review the Privacy Notice.
      </StyledParagraph>
      <StyledH2>2. WHAT ARE COOKIES AND RELATED TECHNOLOGIES</StyledH2>
      <StyledParagraph>
        As is common practice among websites, our Services use cookies, which are tiny files
        downloaded to your device that allow us and our third-party partners to collect certain
        information about your interactions with our email communications, websites and other online
        services, and that improve your experience. We and our third-party partners and providers
        may also use other, related technologies to collect this information, such as web beacons,
        pixels, embedded scripts, location-identifying technologies and logging technologies
        (collectively, <StyledSpan>`“cookies”`</StyledSpan>).
      </StyledParagraph>
      <StyledParagraph>We use the following types of cookies:</StyledParagraph>
      <StyledUnorderedList>
        <StyledListItem>
          (a) <StyledSpan>Strictly necessary cookies.</StyledSpan> These cookies enable core
          functionality such as security, network management and accessibility. You may disable
          these by changing your browser settings, but this may affect how the Services function.
          The legal basis for our use of strictly necessary cookies is our legitimate interests,
          namely being able to provide and maintain our Services.
        </StyledListItem>
        <StyledListItem>
          (b) <StyledSpan>Functional cookies.</StyledSpan> These enable a website to remember
          information that changes the way the website behaves or looks, like your preferred
          language or the region that you are in. The legal basis for our use of functionality
          cookies is our legitimate interests, namely being able to provide and maintain our
          Services.
        </StyledListItem>
        <StyledListItem>
          (c) <StyledSpan>Analytical/performance cookies.</StyledSpan> These cookies allow us to
          recognize and count the number of visitors to our Services, and to see how visitors move
          around our Services when they are using them. This helps us to improve the way our
          Services work, for example, by ensuring that users are finding what they are looking for
          easily. If you are accessing our Services with a European IP address, you have been asked
          to consent to the use of these cookies. You are free to deny your consent.
        </StyledListItem>
        <StyledListItem>
          (d) <StyledSpan>Targeting cookies.</StyledSpan>Targeting cookies. These cookies record
          your visit to our Services, the pages you have visited and the links you have followed.
          They are used to track visitors across our Services. If you are accessing our Services
          with a European IP address, you have been asked to consent to the use of these cookies.
          You are free to deny your consent.
        </StyledListItem>
      </StyledUnorderedList>
      <StyledH2>3. WHAT WE COLLECT WHEN USING COOKIES</StyledH2>
      <StyledParagraph>
        We and our third-party partners and providers may use cookies to automatically collect
        certain types of usage information when you visit or interact with our email communications
        and Services. For example, we may collect log data about your device and its software, such
        as your IP address, operating system, browser type, date/time of your visit, and other
        similar information. Our emails may also contain tracking pixels that identify if and when
        you have opened an email that we have sent you, how many times you have read it and whether
        you have clicked on any links in that email. We may also collect analytics data or use
        third-party analytics tools to help us measure usage and activity trends for our online
        services and better understand our customer base. We also may collect location data,
        including general geographic location based on IP address or more precise location data when
        a user accesses our online services through a mobile device.
      </StyledParagraph>
      <StyledParagraph>
        When you access our Services from a mobile device, we may collect unique identification
        numbers associated with your device or our mobile application (including, for example, a
        UDID, Unique ID for Advertisers (“IDFA”), Google AdID, or Windows Advertising ID), mobile
        carrier, device type, model and manufacturer, mobile device operating system brand and
        model, phone number, and depending on your mobile device settings, your geographical
        location data, including GPS coordinates (e.g., latitude and/or longitude) or similar
        information regarding the location of your mobile device, or we may be able to approximate a
        device&apos;s location by analyzing other information, like an IP address.
      </StyledParagraph>
      <StyledParagraph>
        We may also include or engage in the following as part of our Services:
      </StyledParagraph>
      <StyledUnorderedList>
        <StyledListItem>
          <StyledSpan>Social Media Widgets and Advertising.</StyledSpan> Our Services may include
          social media features, such as the Facebook “Like” button, Reddit, LinkedIn, Twitter or
          other widgets. These social media companies may recognize you and collect information
          about your visit to our Services, and they may set a cookie or employ other tracking
          technologies. Your interactions with those features are governed by the privacy policies
          of those companies.
        </StyledListItem>
        <StyledListItem>
          <StyledSpan>Social Media Platforms.</StyledSpan> We may display targeted advertising to
          you through social media platforms, such as Facebook, Twitter, LinkedIn and other social
          media forums. These companies have interest-based advertising programs that allow us to
          direct advertisements to users who have shown interest in our services while those users
          are on the social media platform, or to groups of other users who share similar traits,
          such as likely commercial interests and demographics. We may share a unique identifier,
          such as a user ID or hashed email address, with these platform providers or they may
          collect information from our website visitors through a first-party pixel, in order to
          direct targeted advertising to you or to a custom audience on the social media platform.
          These advertisements are governed by the privacy policies of those social media companies
          that provide them. If you do not want to receive targeted ads on your social networks, you
          may be able to adjust your advertising preferences through your settings on those
          networks.{' '}
        </StyledListItem>
        <StyledListItem>
          <StyledSpan>Third Party Partners. </StyledSpan>
          We work with a variety of third-party partners to provide advertising services. For
          example, we use Google Analytics to recognize you and link the devices you use when you
          visit our Services on your browser or mobile device, log in to your account on our
          Services, or otherwise engage with us. We share a unique identifier, like a user ID or
          hashed email address, with Google to facilitate the service. Google Analytics allows us to
          better understand how our users interact with our Services and to tailor our
          advertisements and content to you. For information on how Google Analytics collects and
          processes data, as well as how you can control information sent to Google, review
          Google&apos;s website, “How Google uses data when you use our partners&apos; sites or
          apps” located at{' '}
          <StyledLink href={'https://www.google.com/policies/privacy/partners/'}>
            www.google.com/policies/privacy/partners/
          </StyledLink>
          . You can learn about Google Analytics&apos; currently available opt-outs, including the
          Google Analytics Browser Ad-On here:{' '}
          <StyledLink href={'https://tools.google.com/dlpage/gaoptout/'}>
            https://tools.google.com/dlpage/gaoptout/.
          </StyledLink>
          We may also utilize certain forms of display advertising and other advanced features
          through Google Analytics. These features enable us to use first-party cookies (such as the
          Google Analytics cookie) and third-party cookies (such as the DoubleClick advertising
          cookie) or other third-party cookies together to inform, optimize, and display ads based
          on your past visits to the Services. You may control your advertising preferences or
          opt-out of certain Google advertising products by visiting the Google Ads Preferences
          Manager, currently available at{' '}
          <StyledLink href={'https://google.com/ads/preferences'}>
            https://google.com/ads/preferences
          </StyledLink>
          , or by visiting NAI&apos;s online resources at{' '}
          <StyledLink href={'http://www.networkadvertising.org/choices'}>
            https://www.networkadvertising.org/choices
          </StyledLink>
          .
        </StyledListItem>
      </StyledUnorderedList>
      <StyledParagraph>
        You can learn more about our analytics providers&apos; practices at the following URLs:
      </StyledParagraph>
      <StyledUnorderedList>
        <StyledListItem>
          Google Analytics:{' '}
          <StyledLink href={'https://policies.google.com/technologies/partner-sites'}>
            https://policies.google.com/technologies/partner-sites
          </StyledLink>
        </StyledListItem>
        <StyledListItem>
          Amplitude:{' '}
          <StyledLink href={'https://amplitude.com/privacy'}>
            https://amplitude.com/privacy
          </StyledLink>
        </StyledListItem>
        <StyledListItem>
          Metabase:{' '}
          <StyledLink href={'https://www.metabase.com/privacy '}>
            https://www.metabase.com/privacy{' '}
          </StyledLink>
        </StyledListItem>
        <StyledListItem>
          Dune Analytics:{' '}
          <StyledLink href={'https://dune.com/privacy'}>https://dune.com/privacy</StyledLink>
        </StyledListItem>
        <StyledListItem>
          Prodsight:{' '}
          <StyledLink href={'https://www.playvox.com/privacy-policy/ '}>
            https://www.playvox.com/privacy-policy/{' '}
          </StyledLink>
        </StyledListItem>
      </StyledUnorderedList>
      <StyledH2>4. HOW WE USE INFORMATION COLLECTED VIA COOKIES</StyledH2>
      <StyledUnorderedList>
        <StyledParagraph>We use cookies for a variety of reasons outlined below:</StyledParagraph>
        <StyledListItem>
          If you create an account with us, we will use cookies for the management of the signup
          process and general administration. These cookies will usually be deleted when you log
          out; however, in some cases, they may remain in order to remember your site preferences
          when logged out.
        </StyledListItem>
        <StyledListItem>
          We use cookies when you are logged in so that we can remember you. These cookies are
          typically removed or cleared when you log out to ensure you can only access restricted
          features and areas when logged in.
        </StyledListItem>
        <StyledListItem>
          The Services offer newsletter or email subscription services and cookies may be used to
          remember if you are already registered and whether to show certain notifications which
          might only be valid to subscribed/unsubscribed users.
        </StyledListItem>
        <StyledListItem>
          The Services may offer payment capabilities and some cookies are essential to ensure that
          your order is remembered between pages so that we can process it properly.{' '}
        </StyledListItem>
        <StyledListItem>
          When you submit data through a form, such as those found on the contact pages or comment
          forms, cookies may be set to remember your user details for future correspondence.{' '}
        </StyledListItem>
        <StyledListItem>
          In order to provide you with a great experience on the Services, we provide the
          functionality to set your preferences for how the Services run when you use it. In order
          to remember your preferences, we need to set cookies so that this information can be
          called whenever you interact with a website page.
        </StyledListItem>
        <StyledListItem>
          We use cookies to provide and monitor the effectiveness of our Services, monitor online
          usage and activities of our Services, and facilitate the purposes identified in the{' '}
          <StyledSpan>How We Use Your Personal Information </StyledSpan> section of our Privacy
          Notice.
        </StyledListItem>
        <StyledListItem>
          We may also use the information we collect through cookies to understand your browsing
          activities, including across unaffiliated third-party sites, so that we can deliver
          information about products and services that may be of interest to you.{' '}
        </StyledListItem>
        <StyledListItem>
          Tracking technology used in emails helps us measure the effectiveness of our marketing
          email campaigns, make the emails we send to you more relevant to your interests and help
          us understand if you have opened and how you interacted with our email.{' '}
        </StyledListItem>
      </StyledUnorderedList>
      <StyledParagraph>
        Please note that we link some of the personal information we collect through cookies with
        the other personal information that we collect about you and for the purposes described in
        our Privacy Notice.
      </StyledParagraph>
      <StyledH2>5. YOUR CHOICES ABOUT COOKIES</StyledH2>
      <StyledParagraph>
        If you would prefer not to accept cookies, most browsers will allow you to change the
        setting of cookies by adjusting the settings on your browser to: (i) notify you when you
        receive a cookie, which lets you choose whether or not to accept it; (ii) disable existing
        cookies; or (iii) set your browser to automatically reject cookies. Be aware that disabling
        cookies may negatively affect the functionality of this and many other websites that you
        visit. Disabling cookies will usually result in also disabling certain functionalities and
        features of the Services.
      </StyledParagraph>
      <StyledParagraph>
        Depending on your device and operating system, you may not be able to delete or block all
        cookies. In addition, if you want to reject cookies across all your browsers and devices,
        you will need to do so on each browser on each device you actively use. You may also set
        your email options to prevent the automatic downloading of images that may contain
        technologies that would allow us to know whether you have accessed our email and performed
        certain functions with it.
      </StyledParagraph>
      <StyledParagraph>
        <StyledSpan>Online Ads: </StyledSpan> To learn more about interest-based advertising and how
        you may be able to opt-out of some of this advertising, you may wish to visit the Digital
        Advertising Alliance&apos;s (DAA) resources and/or the Network Advertising Initiative&apos;s
        (NAI) online resources, at{' '}
        <StyledLink href={'https://www.aboutads.info/choices'}>
          https://www.aboutads.info/choices
        </StyledLink>
        , or
        <StyledLink href={'https://www.networkadvertising.org/choices/'}>
          https://www.networkadvertising.org/choices/
        </StyledLink>
        , or if you are in the European Economic Area, United Kingdom or Switzerland:
        <StyledLink href={'https://youronlinechoices.eu/'}>
          https://youronlinechoices.eu/
        </StyledLink>
        . You may also be able to limit interest-based advertising through the settings menu on your
        mobile device by selecting “limit ad tracking” (iOS) or “opt-out of interest-based ads”
        (Android). You may also be able to opt-out of some — but not all — interest-based
        advertising served by mobile ad networks by visiting{' '}
        <StyledLink href={'https://youradchoices.com/appchoices'}>
          https://youradchoices.com/appchoices
        </StyledLink>{' '}
        and downloading the mobile AppChoices app.
      </StyledParagraph>
      <StyledParagraph>
        Please note that when you opt out of receiving interest-based advertisements, this does not
        mean you will no longer see advertisements from us or on our online services. It means that
        the online ads that you do see from DAA program participants should not be based on your
        interests. We are not responsible for the effectiveness of, or compliance with, any
        third-parties&apos; opt-out options or programs or the accuracy of their statements
        regarding their programs. In addition, third parties may still use cookies to collect
        information about your use of our online services, including for analytics and fraud
        prevention as well as any other purpose permitted under the DAA&apos;s Principles.
      </StyledParagraph>
      <StyledParagraph>
        If you are located in the EEA, UK, or Switzerland, you may take advantage of{' '}
        <StyledSpan>Your Online Choices.</StyledSpan> This service allows you to select tracking
        preferences for most of the advertising tools. As such, it is recommended that you make use
        of this resource in addition to the information provided in this document.
      </StyledParagraph>
    </div>
  </div>
);
